import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TransferActionError,
  fetchTransferRecord,
  newTransferRequestKey,
  toTransferActionError,
  transferRecordQueryKeys,
  type TransferRecord,
} from '@/data/transferRecord';
import { invalidateAvailabilityQueries } from '../../../hooks/mutations/useInventoryMutations';

/** The older transfer list queries (calendar, add-to-transfer) are keyed under this prefix. */
const TRANSFER_ORDERS_PREFIX = ['transfer-orders'] as const;

/** The transfer record (one round trip), cached under ['warehouse', 'transfer-record', id]. */
export function useTransferRecordQuery(transferId: string) {
  return useQuery<TransferRecord | null>({
    queryKey: transferRecordQueryKeys.record(transferId),
    queryFn: () => fetchTransferRecord(transferId),
    enabled: Boolean(transferId),
    staleTime: 15_000,
  });
}

/**
 * `retry`: resend the slot's attempt that got no answer, exactly as it was first sent (payload and
 * request key), whatever the caller's payload is now. `new`: a new action with a new key. Left out,
 * a call that repeats that attempt's payload exactly is taken as its retry, anything else is new.
 */
export type TransferActionAttempt = 'retry' | 'new';

export type TransferActionRunOptions = {
  /** Stock moved or demand changed (lifecycle and line actions): availability reads refresh too. */
  movesStock?: boolean;
  /** The failure message when the server gives none. */
  fallback: string;
  attempt?: TransferActionAttempt;
};

export type TransferActionRunner = {
  /**
   * Runs one user action with its request key: one key per payload sent. A call the server
   * answered is done with its key, success or refusal (a refused call changed nothing). A call that
   * got no answer may have applied, so its payload and key are kept for a retry, which sends both
   * again unchanged: the server then replays the first result instead of applying it twice.
   * Failures throw TransferActionError (a readable message; `answered` says whether the server
   * answered).
   */
  run: <P, T>(
    slot: string,
    payload: P,
    call: (requestKey: string, payload: P) => Promise<T>,
    options: TransferActionRunOptions,
  ) => Promise<T>;
  /** True while any action is in flight. */
  busy: boolean;
  /** Reloads the record and every Warehouse list. */
  refresh: (movesStock?: boolean) => Promise<void>;
  /** Forgets the unanswered attempts of every slot in a scope (a dialog that closed). */
  forget: (scope: string) => void;
};

type Attempt = { fingerprint: string; payload: unknown; key: string };

function fingerprint(payload: unknown): string {
  try {
    return JSON.stringify(payload) ?? '';
  } catch {
    return String(Math.random());
  }
}

export function useTransferActionRunner(transferId: string): TransferActionRunner {
  const queryClient = useQueryClient();
  const attemptsRef = React.useRef(new Map<string, Attempt>());
  const [inFlight, setInFlight] = React.useState(0);

  React.useEffect(() => {
    attemptsRef.current.clear();
  }, [transferId]);

  const refresh = React.useCallback(async (movesStock = false) => {
    if (movesStock) invalidateAvailabilityQueries(queryClient);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['warehouse'] }),
      queryClient.invalidateQueries({ queryKey: TRANSFER_ORDERS_PREFIX }),
      queryClient.invalidateQueries({ queryKey: ['record-audit', 'transfer', transferId] }),
    ]);
  }, [queryClient, transferId]);

  const run = React.useCallback(async function runTransferAction<P, T>(
    slot: string,
    payload: P,
    call: (requestKey: string, payload: P) => Promise<T>,
    options: TransferActionRunOptions,
  ): Promise<T> {
    const attempts = attemptsRef.current;
    const print = fingerprint(payload);
    const pending = attempts.get(slot);
    if (options.attempt === 'retry' && !pending) {
      // Nothing to resend (its dialog closed): sending the payload under a new key could apply it twice.
      throw new TransferActionError('This was already sent once and may have gone through. Check the record, then count again.', {
        answered: true,
      });
    }
    const resend = pending !== undefined && options.attempt !== 'new'
      && (options.attempt === 'retry' || pending.fingerprint === print);
    const attempt: Attempt = resend && pending ? pending : { fingerprint: print, payload, key: newTransferRequestKey() };
    // Kept while in flight too, so a second press of the same thing waits on the server for the first.
    attempts.set(slot, attempt);
    setInFlight((count) => count + 1);
    try {
      const result = await call(attempt.key, attempt.payload as P);
      if (attempts.get(slot) === attempt) attempts.delete(slot);
      return result;
    } catch (error) {
      const actionError = toTransferActionError(error, options.fallback);
      // The server answered: the call rolled back and its key is spent. No answer: keep it for a retry.
      if (actionError.answered && attempts.get(slot) === attempt) attempts.delete(slot);
      throw actionError;
    } finally {
      setInFlight((count) => Math.max(0, count - 1));
      // Success or refusal, the record may have moved on (someone else acted): reload it.
      void refresh(Boolean(options.movesStock));
    }
  }, [refresh]);

  const forget = React.useCallback((scope: string) => {
    const prefix = `${scope}:`;
    for (const slot of Array.from(attemptsRef.current.keys())) {
      if (slot.startsWith(prefix)) attemptsRef.current.delete(slot);
    }
  }, []);

  return { run, busy: inFlight > 0, refresh, forget };
}

/**
 * The runner for one dialog session: its slots live under `scope`, and their unanswered attempts
 * are forgotten when the scope changes or unmounts, so opening the dialog again is a new action.
 */
export function useTransferActionScope(runner: TransferActionRunner, scope: string): TransferActionRunner {
  const { run, busy, refresh, forget } = runner;
  React.useEffect(() => () => forget(scope), [forget, scope]);
  return React.useMemo(() => {
    function scopedRun<P, T>(
      slot: string,
      payload: P,
      call: (requestKey: string, payload: P) => Promise<T>,
      options: TransferActionRunOptions,
    ): Promise<T> {
      return run(`${scope}:${slot}`, payload, call, options);
    }
    return { run: scopedRun, busy, refresh, forget };
  }, [busy, forget, refresh, run, scope]);
}
